import {
  Box,
  Collapse,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import {
  completeCriticalNoticeCall,
  getExpandData,
  saveExpand,
  type CriticalNoticeCallListItem,
} from "Services/BloodLisCriticalNoticeCall/index";
import LoadButton from "Shared/Common/LoadButton";
import { Loader } from "Shared/Common/Loader";
import Status from "Shared/Common/Status";
import useLang from "Shared/hooks/useLanguage";
import { AddIcon, RemoveICon } from "Shared/Icons";
import PermissionComponent, {
  getPermissionDisplayName,
} from "Shared/Common/Permissions/PermissionComponent";

function getCellValue(row: any, columnKey: string): any {
  return row?.[columnKey];
}

function getRequisitionStatusClass(status: string): string {
  const statusClassMap: Record<string, string> = {
    "Specimen Collected": "badge-status-specimen-collected",
    Processing: "badge-status-processing",
    "Result Available": "badge-status-result-available",
    Complete: "badge-status-complete",
    Deleted: "badge-status-deleted",
    Validated: "badge-status-validated",
    "Save For Signature": "badge-status-waiting-for-Signature",
    "On Hold": "badge-status-hold",
    "Missing Info": "badge-status-missing-info",
    "In Transit": "badge-status-in-transit",
    Canceled: "badge-status-canceled",
    Approved: "badge-status-approved",
    Pending: "badge-status-pending",
    Rejected: "badge-status-rejected",
    Shipped: "badge-status-shipped",
  };
  return statusClassMap[status] || "badge-status-default";
}

/** Same LIS status badge mapping as Blood Result Data grid */
function getLisStatusClass(
  status: string,
  translate: (key: string) => string
): string {
  if (status === translate("Ready to Report")) {
    return "badge-status-result-available";
  }
  if (status === translate("Amended")) {
    return "badge-status-complete";
  }
  if (status === translate("Ready to Validate")) {
    return "badge-status-validated";
  }
  if (status === translate("Final")) {
    return "badge-status-hold";
  }
  if (status === translate("Corrected")) {
    return "badge-status-in-transit";
  }
  if (status === translate("Pending")) {
    return "badge-status-pending";
  }
  if (status === translate("Ready to Publish")) {
    return "badge-status-shipped";
  }
  return "badge-status-default";
}

const NormalizedData = (obj: any, key: string) =>
  obj[
    Object.keys(obj).find((k) => k.toLowerCase() === key.toLowerCase()) || ""
  ];

function toDateTimeLocalValue(value?: string): string {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(
    d.getDate()
  )}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function toScheduledDate(value?: string): string {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n: number, len = 2) => String(n).padStart(len, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(
    d.getDate()
  )}T${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}.${pad(
    d.getMilliseconds(),
    3
  )}`;
}

/** Used for Complete visibility: only sync from API, not from unsaved form edits */
function listHasAllReadBackComplete(
  list: CriticalNoticeCallListItem[]
): boolean {
  return (
    list.length > 0 &&
    list.every((item) => String(item.readBack ?? "").trim().length > 0)
  );
}

interface CriticalNoticeCallRowProps {
  row: any;
  tabsInfo: any[];
  activeTabName: string;
  isExpanded: boolean;
  onToggleExpand: () => void;
  onOrderCompleted?: () => void;
}

const CriticalNoticeCallRow: React.FC<CriticalNoticeCallRowProps> = ({
  row,
  tabsInfo,
  activeTabName,
  isExpanded,
  onToggleExpand,
  onOrderCompleted,
}) => {
  const { t } = useLang();
  const [expandData, setExpandData] = useState<{
    name: string;
    email: string;
    phoneNo: string;
    criticalNoticeCallLists: CriticalNoticeCallListItem[];
  } | null>(null);
  const [loadingExpand, setLoadingExpand] = useState(false);
  const [saving, setSaving] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [readBackCompleteAsOfServer, setReadBackCompleteAsOfServer] =
    useState(false);

  const reqOrderId = row?.RequisitionOrderId;

  useEffect(() => {
    if (isExpanded && reqOrderId !== undefined && reqOrderId !== null) {
      setLoadingExpand(true);
      getExpandData(reqOrderId)
        .then((res: any) => {
          const d = res?.data?.data;
          if (d) {
            const criticalNoticeCallLists = Array.isArray(
              d.criticalNoticeCallLists
            )
              ? d.criticalNoticeCallLists
              : [];
            setExpandData({
              name: d.name ?? "",
              email: d.email ?? "",
              phoneNo: d.phoneNo ?? "",
              criticalNoticeCallLists,
            });
            setReadBackCompleteAsOfServer(
              listHasAllReadBackComplete(criticalNoticeCallLists)
            );
          } else {
            setExpandData({
              name: "",
              email: "",
              phoneNo: "",
              criticalNoticeCallLists: [],
            });
            setReadBackCompleteAsOfServer(false);
          }
        })
        .catch(() => {
          setExpandData({
            name: "",
            email: "",
            phoneNo: "",
            criticalNoticeCallLists: [],
          });
          setReadBackCompleteAsOfServer(false);
        })
        .finally(() => setLoadingExpand(false));
    }
  }, [isExpanded, reqOrderId]);

  const handleListChange = (
    index: number,
    field: keyof CriticalNoticeCallListItem,
    value: string | number
  ) => {
    setExpandData((prev) => {
      if (!prev) return prev;
      const list = [...prev.criticalNoticeCallLists];
      if (list[index]) list[index] = { ...list[index], [field]: value };
      return { ...prev, criticalNoticeCallLists: list };
    });
  };

  const buildExpandPayload = (): CriticalNoticeCallListItem[] | null => {
    const list = expandData?.criticalNoticeCallLists;
    if (!list?.length) return null;
    return list.map((item: any) => {
      const { dateAndTime, ...rest } = item;
      return {
        ...rest,
        dateAndTime: toScheduledDate(dateAndTime),
      };
    }) as CriticalNoticeCallListItem[];
  };

  /** Person Notified (notifyTo) and Read Back Complete (readBack) required on every row */
  const getNotifyAndReadBackValidationMessage = (
    list: CriticalNoticeCallListItem[]
  ): string | null => {
    if (!list.length) return null;
    const missingNotify = list.some(
      (item) => String(item.notifyTo ?? "").trim().length === 0
    );
    const missingReadBack = list.some(
      (item) => String(item.readBack ?? "").trim().length === 0
    );
    if (missingNotify && missingReadBack) {
      return t(
        "Person Notified and Read Back Complete are required for all tests."
      );
    }
    if (missingNotify) {
      return t("Person Notified is required for all tests.");
    }
    if (missingReadBack) {
      return t("Read Back Complete is required for all tests.");
    }
    return null;
  };

  const refreshExpandData = async () => {
    if (reqOrderId === undefined || reqOrderId === null) return;
    setLoadingExpand(true);
    try {
      const res: any = await getExpandData(reqOrderId);
      const d = res?.data?.data;
      if (d) {
        const criticalNoticeCallLists = Array.isArray(d.criticalNoticeCallLists)
          ? d.criticalNoticeCallLists
          : [];
        setExpandData({
          name: d.name ?? "",
          email: d.email ?? "",
          phoneNo: d.phoneNo ?? "",
          criticalNoticeCallLists,
        });
        setReadBackCompleteAsOfServer(
          listHasAllReadBackComplete(criticalNoticeCallLists)
        );
      } else {
        setExpandData({
          name: "",
          email: "",
          phoneNo: "",
          criticalNoticeCallLists: [],
        });
        setReadBackCompleteAsOfServer(false);
      }
    } catch {
      setExpandData({
        name: "",
        email: "",
        phoneNo: "",
        criticalNoticeCallLists: [],
      });
      setReadBackCompleteAsOfServer(false);
    } finally {
      setLoadingExpand(false);
    }
  };

  const handleSave = async () => {
    const list = expandData?.criticalNoticeCallLists ?? [];
    if (!list.length) {
      toast.info(t("No data to save."));
      return;
    }
    const validationMsg = getNotifyAndReadBackValidationMessage(list);
    if (validationMsg) {
      toast.error(validationMsg);
      return;
    }
    const payload = buildExpandPayload();
    if (!payload) {
      toast.info(t("No data to save."));
      return;
    }
    setSaving(true);
    try {
      const res: any = await saveExpand(payload as any);
      if (res?.data?.statusCode === 200 || res?.data?.statusCode === 100) {
        toast.success(t("Saved successfully."));
        // Refresh expanded data after save (so UI stays in sync).
        await refreshExpandData();
      } else {
        toast.error(t(res?.data?.message ?? "Failed to save."));
      }
    } catch {
      toast.error(t("Failed to save."));
    } finally {
      setSaving(false);
    }
  };

  const handleComplete = async () => {
    const list = expandData?.criticalNoticeCallLists ?? [];
    if (!list.length) {
      toast.info(t("No data to complete."));
      return;
    }
    const validationMsg = getNotifyAndReadBackValidationMessage(list);
    if (validationMsg) {
      toast.error(validationMsg);
      return;
    }
    const payload = buildExpandPayload();
    if (!payload) {
      toast.info(t("No data to complete."));
      return;
    }
    setCompleting(true);
    try {
      const res: any = await completeCriticalNoticeCall(payload);
      if (res?.data?.statusCode === 200 || res?.data?.statusCode === 100) {
        toast.success(t("Call completed successfully."));
        onOrderCompleted?.();
      } else {
        toast.error(t(res?.data?.message ?? "Failed to complete."));
      }
    } catch {
      toast.error(t("Failed to complete."));
    } finally {
      setCompleting(false);
    }
  };

  const visibleTabs = Array.isArray(tabsInfo)
    ? tabsInfo.filter(
        (t: any) => t?.isShowOnUi !== false && t?.isShow !== false
      )
    : [];
  const isClosedCallTab = String(activeTabName ?? "")
    .trim()
    .toLowerCase()
    .includes("closed");
  const showCompleteButton = readBackCompleteAsOfServer && !isClosedCallTab;

  const openInNewTab = (url: string) => {
    window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <>
      <TableRow>
        <TableCell className="w-50px" sx={{ verticalAlign: "middle" }}>
          <div className="d-flex justify-content-center">
            <IconButton
              aria-label="expand row"
              size="small"
              onClick={onToggleExpand}
              className={`btn btn-icon btn-icon-light btn-sm fw-bold rounded h-20px w-20px min-h-20px ${
                isExpanded ? "btn-table-expend-row" : "btn-primary"
              }`}
            >
              {isExpanded ? <RemoveICon /> : <AddIcon />}
            </IconButton>
          </div>
        </TableCell>
        {visibleTabs.map((tabsDetail: any) => {
          const colKeyLower = String(tabsDetail?.columnKey ?? "").toLowerCase();
          const isLisStatusCol = colKeyLower === "lisstatus";
          return (
            <TableCell
              key={tabsDetail.columnKey}
              {...(isLisStatusCol
                ? {
                    id: `CriticalNoticeCallLISStatus_${row?.RequisitionOrderId}`,
                    sx: { width: "max-content", textAlign: "center" },
                  }
                : {})}
            >
              {tabsDetail?.columnKey === "View" ? (
                <div className="d-flex justify-content-center">
                  <PermissionComponent
                    moduleName="Blood LIS"
                    pageName="Critical Notice Call"
                    permissionIdentifier="View"
                  >
                    {({ pageName, moduleName, permissionIdentifier }) => (
                      <button
                        role="link"
                        className="btn btn-sm fw-bold btn-warning fs-12px py-0 fw-500 align-items-center d-flex"
                        onClick={() => {
                          const tokenData: any =
                            sessionStorage.getItem("userinfo");
                          localStorage.setItem("userinfo", tokenData);
                          openInNewTab(
                            `/OrderView/${btoa(row?.RequisitionId)}/${btoa(
                              NormalizedData(row, "requisitionorderid")
                            )}`
                          );
                        }}
                      >
                        {getPermissionDisplayName(
                          pageName,
                          moduleName,
                          permissionIdentifier,
                          "View"
                        )}
                      </button>
                    )}
                  </PermissionComponent>
                </div>
              ) : tabsDetail?.columnKey === "RequisitionStatus" ? (
                <Status
                  cusText={String(getCellValue(row, "RequisitionStatus") ?? "")}
                  cusClassName={getRequisitionStatusClass(
                    String(getCellValue(row, "RequisitionStatus") ?? "")
                  )}
                />
              ) : isLisStatusCol ? (
                <Status
                  cusText={String(
                    getCellValue(row, tabsDetail.columnKey) ?? ""
                  )}
                  cusClassName={getLisStatusClass(
                    String(getCellValue(row, tabsDetail.columnKey) ?? ""),
                    t
                  )}
                />
              ) : (
                (getCellValue(row, tabsDetail.columnKey) ?? "")
              )}
            </TableCell>
          );
        })}
      </TableRow>
      <TableRow>
        <TableCell
          style={{ paddingBottom: 0, paddingTop: 0 }}
          colSpan={visibleTabs.length + 1}
        >
          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
            <Box sx={{ margin: 2 }}>
              <div className="mb-4">
                <div className="fw-bold mb-2">
                  {t("Critical Contact Information")}
                </div>
                <div className="row g-3">
                  <div className="col-md-2">
                    <label className="form-label">{t("Name")}</label>
                    <input
                      type="text"
                      className="form-control rounded-2 bg-light"
                      style={{ backgroundColor: "#E8F4FD" }}
                      value={expandData?.name ?? ""}
                      readOnly
                      disabled
                    />
                  </div>
                  <div className="col-md-2">
                    <label className="form-label">{t("Email")}</label>
                    <input
                      type="text"
                      className="form-control rounded-2 bg-light"
                      style={{ backgroundColor: "#E8F4FD" }}
                      value={expandData?.email ?? ""}
                      readOnly
                      disabled
                    />
                  </div>
                  <div className="col-md-2">
                    <label className="form-label">{t("Phone No")}</label>
                    <input
                      type="text"
                      className="form-control rounded-2 bg-light"
                      style={{ backgroundColor: "#E8F4FD" }}
                      value={expandData?.phoneNo ?? ""}
                      readOnly
                      disabled
                    />
                  </div>
                </div>
              </div>
              <div className="mb-3">
                <div className="fw-bold text-dark mb-2">{t("Result")}</div>
                <div className="d-flex flex-wrap align-items-center gap-2">
                  <LoadButton
                    id="BloodCriticalNoticeCallExpandSave"
                    className="btn btn-primary btn-sm fw-bold search fs-12px h-30px py-0 fw-500"
                    btnText={t("Save")}
                    loadingText={t("Saving")}
                    loading={saving}
                    disabled={completing}
                    onClick={handleSave}
                  />
                  {showCompleteButton ? (
                    <LoadButton
                      id="BloodCriticalNoticeCallExpandComplete"
                      className="btn btn-linkedin btn-sm fw-bold fs-12px h-30px py-0 fw-500 text-white"
                      btnText={t("Complete")}
                      loadingText={t("Completing")}
                      loading={completing}
                      disabled={saving}
                      onClick={handleComplete}
                    />
                  ) : null}
                </div>
              </div>
              <div
                className="col-xl-12 col-lg-12 col-md-12 col-sm-12 rounded mb-2"
                style={{ border: "2px solid #7239ea" }}
              >
                <div className="card mb-4 border">
                  <div className="card-header bg-light-info d-flex justify-content-between align-items-center px-4 min-h-40px">
                    <h5 className="m-0 text-info">
                      {t("Patient Test Results")}
                    </h5>
                  </div>
                  <div className="card-body py-md-4 py-3 px-4">
                    <div className="table_bordered overflow-hidden">
                      <TableContainer className="shadow-none">
                        <Table
                          stickyHeader
                          size="small"
                          aria-label="patient test results table"
                          sx={{
                            "& .MuiTableCell-root": { py: 0.5 },
                          }}
                        >
                          <TableHead
                            className="h-35px"
                            sx={{
                              "& .MuiTableCell-root, & .MuiTableCell-head, & .MuiTableCell-stickyHeader":
                                {
                                  backgroundColor: "#ffeac3 !important",
                                  color: "#000 !important",
                                },
                              "& .MuiTableCell-root *": {
                                color: "#000 !important",
                              },
                            }}
                          >
                            <TableRow
                              sx={{
                                "& .MuiTableCell-root": {
                                  py: 0.75,
                                  whiteSpace: "nowrap",
                                  color: "#000 !important",
                                },
                              }}
                            >
                              <TableCell>{t("Panel Name")}</TableCell>
                              <TableCell>{t("Test Name")}</TableCell>
                              <TableCell>{t("Units")}</TableCell>
                              <TableCell>{t("Flag")}</TableCell>
                              <TableCell>{t("Result Value")}</TableCell>
                              <TableCell>
                                {t("Responsible Individual for Communicating")}
                              </TableCell>
                              <TableCell>{t("Date & Time")}</TableCell>
                              <TableCell>{t("Person Notified")}</TableCell>
                              <TableCell>{t("Read Back Complete")}</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {loadingExpand ? (
                              <TableRow>
                                <TableCell colSpan={9} className="padding-0">
                                  <Loader />
                                </TableCell>
                              </TableRow>
                            ) : (expandData?.criticalNoticeCallLists?.length ??
                                0) > 0 ? (
                              (expandData?.criticalNoticeCallLists ?? []).map(
                                (r, idx) => (
                                  <TableRow key={idx}>
                                    <TableCell>{r.panelName}</TableCell>
                                    <TableCell>{r.testName}</TableCell>
                                    <TableCell>{r.units}</TableCell>
                                    <TableCell>{r.flag}</TableCell>
                                    <TableCell>{r.resultValue}</TableCell>
                                    <TableCell>{r.notifyBy}</TableCell>
                                    <TableCell className="p-0 align-middle">
                                      <input
                                        type="datetime-local"
                                        className="form-control form-control-sm py-0"
                                        style={{
                                          height: 28,
                                          minHeight: 28,
                                          paddingLeft: 8,
                                          paddingRight: 8,
                                          backgroundColor: "#fff",
                                          border: "1px solid #dee2e6",
                                          borderRadius: 4,
                                        }}
                                        value={toDateTimeLocalValue(
                                          r.dateAndTime
                                        )}
                                        onChange={(e) =>
                                          handleListChange(
                                            idx,
                                            "dateAndTime",
                                            e.target.value
                                          )
                                        }
                                      />
                                    </TableCell>
                                    <TableCell className="p-0 align-middle">
                                      <input
                                        type="text"
                                        className="form-control form-control-sm py-0"
                                        style={{
                                          height: 28,
                                          minHeight: 28,
                                          paddingLeft: 8,
                                          paddingRight: 8,
                                          backgroundColor: "#fff",
                                          border: "1px solid #dee2e6",
                                          borderRadius: 4,
                                        }}
                                        value={r.notifyTo ?? ""}
                                        onChange={(e) =>
                                          handleListChange(
                                            idx,
                                            "notifyTo",
                                            e.target.value
                                          )
                                        }
                                      />
                                    </TableCell>
                                    <TableCell className="p-0 align-middle">
                                      <input
                                        type="text"
                                        className="form-control form-control-sm py-0"
                                        style={{
                                          height: 28,
                                          minHeight: 28,
                                          paddingLeft: 8,
                                          paddingRight: 8,
                                          backgroundColor: "#fff",
                                          border: "1px solid #dee2e6",
                                          borderRadius: 4,
                                        }}
                                        value={r.readBack ?? ""}
                                        onChange={(e) =>
                                          handleListChange(
                                            idx,
                                            "readBack",
                                            e.target.value
                                          )
                                        }
                                      />
                                    </TableCell>
                                  </TableRow>
                                )
                              )
                            ) : (
                              <TableRow>
                                <TableCell colSpan={9} className="text-center">
                                  {t("No patient test results available")}
                                </TableCell>
                              </TableRow>
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </div>
                  </div>
                </div>
              </div>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

export default CriticalNoticeCallRow;
